import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isDeveloperUser } from '@/lib/companyVertical';
import { eliminarOSuspender } from '@/lib/destruccion';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import VerSuspendidos from '@/components/VerSuspendidos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Target, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Trash2, DollarSign, User, Calendar, Pencil, XCircle, AlertTriangle, Snowflake, History, MessageSquare, Building2, ClipboardList, Bell, Sparkles, Phone, Mail, Archive } from 'lucide-react';
import RegisterActivityDialog from '@/components/activities/RegisterActivityDialog';
import ActivitiesBellDialog from '@/components/activities/ActivitiesBellDialog';
import HelpTip from '@/components/HelpTip';
import { vendorsVisibleForGroup, groupsVisibleForVendor, memberIdsOfGroup, byFacet, idsOf } from '@/lib/filterCascade';
import { isResurrectedOpp } from '@/lib/leadDuplicate';
import { LimitUpgradeDialog } from '@/components/LimitUpgradeDialog';
import PhoneInput from '@/components/PhoneInput';
import RulesPage from '@/pages/RulesPage';
import FilterField from '@/components/filters/FilterField';

const PRIORITIES = [
  { value: 'high', label: 'Alta', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  { value: 'medium', label: 'Media', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  { value: 'low', label: 'Baja', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
];

const LEAD_SOURCES = ['manual', 'landing', 'whatsapp', 'campaña', 'referido', 'webhook'];

// Campo de inventario del modal "Crear Lead": la VERTICAL propone, `allowed_modules` VETA.
// La vertical la manda el backend (user.dashboard_vertical). Se elige el PRIMER módulo de la
// lista que el tenant NO tenga apagado (default-ON: `!== false`, MISMA semántica que
// showProductFilter y que el comportamiento previo — un perfil sparse/{} NO oculta el campo);
// sin match → SIN campo (no hay else "Propiedad" cajón de sastre). 'developments' NO entra
// acá: su Unidad la pinta el bloque isDev aparte (decisión LOCKED), para no duplicar el campo.
const INVENTORY_BY_VERTICAL = {
  real_estate: ['properties'],
  automotive: ['car_dealer'],
  education: ['products'],
  generic: ['products', 'properties'],
};

const isQuoteStageName = (name) => String(name || '').toLowerCase().includes('cotiz');

const emptyOpp = { title: '', lead_id: '', lead_name: '', lead_email: '', lead_phone: '', lead_source: 'manual', property_id: '', stage_id: '', value: 0, currency: 'MXN', probability: 50, expected_close_date: '', assigned_to: '', priority: 'medium', tags: [], custom_fields: {}, lost_reason: '', lost_reason_detail: '', dev_unit_id: '', dev_unit_label: '', development_id: '' };

export default function OpportunitiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDev = isDeveloperUser(user);
  const isAdmin = user?.role === 'admin';
  const seccion = isAdmin && params.get('seccion') === 'reglas' ? 'reglas' : 'pipeline';
  const setSeccion = (id) => {
    const next = new URLSearchParams(params);
    if (id === 'pipeline') next.delete('seccion');
    else next.set('seccion', id);
    setParams(next, { replace: true });
  };
  const [opps, setOpps] = useState([]);
  const [stages, setStages] = useState([]);
  const [team, setTeam] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [devUnits, setDevUnits] = useState([]);
  const [devs, setDevs] = useState([]);
  const [rouletteGroups, setRouletteGroups] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [oppAiActions, setOppAiActions] = useState([]);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostTarget, setLostTarget] = useState(null);
  const [lostForm, setLostForm] = useState({ lost_reason: '', lost_sub_reason: '', lost_reason_detail: '' });
  const [coldDialogOpen, setColdDialogOpen] = useState(false);
  // Registro de actividad comercial.
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityContext, setActivityContext] = useState({ leadId: null, opportunityId: null, leadName: '' });
  // WS-ACTIVIDADES-2: campana de actividades + Coach IA.
  const [bellOpen, setBellOpen] = useState(false);
  const [bellPending, setBellPending] = useState(0);
  const [leadActivities, setLeadActivities] = useState({});
  // B2: panel de actividades fijado a UN lead (botón en la tarjeta del Kanban).
  const [leadActivitiesView, setLeadActivitiesView] = useState({ open: false, leadId: null, leadName: '' });
  // Filtros del Kanban (cliente).
  const _firstDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
  const _today = () => new Date().toISOString().slice(0,10);
  // Fase 4.5: lo suspendido se oculta por defecto.
  const [verSuspendidos, setVerSuspendidos] = useState(false);
  const [oppSearch, setOppSearch] = useState('');
  const [oppDateFrom, setOppDateFrom] = useState(_firstDay());
  const [oppDateTo, setOppDateTo] = useState(_today());
  const [oppProductFilter, setOppProductFilter] = useState('all');
  const [oppGroupFilter, setOppGroupFilter] = useState('all');
  const [oppOperationFilter, setOppOperationFilter] = useState('all');
  const [oppTeamFilter, setOppTeamFilter] = useState('all');
  const [oppVendorFilter, setOppVendorFilter] = useState('all');
  // Vertical (la manda el backend) + allowedModules → condicionales.
  const vertical = user?.dashboard_vertical || 'generic';
  const allowedModules = user?.allowed_modules || user?.company?.allowed_modules || {};
  // Módulo de inventario a mostrar en el modal (la vertical propone, allowed_modules veta).
  // default-ON (`!== false`): sin la clave explícita el campo SÍ se muestra (no regresa Homely).
  const inventoryModule = (INVENTORY_BY_VERTICAL[vertical] || []).find(k => allowedModules[k] !== false) || null;
  const showProductFilter = (allowedModules.products !== false) && (vertical === 'automotive' || (properties?.length || 0) > 0);
  const showGroupFilter = (devs?.length || 0) > 0;
  // tipo-operacion-renta-venta: gate por VERTICAL (dashboard_vertical), no por
  // allowedModules -- mismo criterio que LeadsPage.js/SettingsPage.js.
  const showOperationFilter = vertical === 'developments' || vertical === 'real_estate';
  const isVendorRole = user?.role === 'vendor';
  // Snapshot ligero del estado de actividades pendientes para badges del bell
  // y animaciones en tarjetas. Refresca cada vez que se abre/cierra el bell
  // y al montar la página.
  useEffect(() => {
    let cancel = false;
    const fetchSnapshot = async () => {
      try {
        // FIX regresión B1: `time_filter: 'pending'` ya no existe desde D3
        // (backend solo reconoce `today`) — quedó como no-op y esta query
        // traía CUALQUIER actividad reciente "mía", sin filtrar por status.
        // `status: 'pending'` es el param real desde D2/D3.
        const res = await api.get('/activities', { params: { scope: 'mine', status: 'pending', limit: 100 } });
        if (cancel) return;
        const items = res.data?.items || [];
        setBellPending(res.data?.pending_count || 0);
        // Index por lead_id. FIX B1: contaba CUALQUIER actividad del batch
        // (incluidas ya realizadas/notas) sin chequear status — con el query
        // ya filtrado arriba esto es redundante pero se deja explícito por
        // si el backend alguna vez devuelve algo fuera de status=pending.
        const idx = {};
        items.forEach(a => {
          if (a.status !== 'pending') return;
          const lid = a.lead_id;
          if (!lid) return;
          if (!idx[lid]) idx[lid] = { count_pending: 0, last_at: a.created_at, last_type: a.type };
          idx[lid].count_pending += 1;
        });
        setLeadActivities(idx);
      } catch {
        /* silencioso */
      }
    };
    fetchSnapshot();
    return () => { cancel = true; };
  }, [bellOpen]);
  const [coldTarget, setColdTarget] = useState(null);
  const [coldForm, setColdForm] = useState({ reactivation_date: '', return_stage_id: '', notes: '' });
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerTarget, setOfferTarget] = useState(null);
  const [offerForm, setOfferForm] = useState({ amount: 0, currency: 'MXN', offered_by: 'cliente', notes: '' });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [isStageAdminCollapsed, setIsStageAdminCollapsed] = useState(true);
  const [leadLimitDialogOpen, setLeadLimitDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyOpp);
  const leadLimit = Number(user?.license_profile?.limits?.contacts ?? -1);
  const isLeadLimitReached = Number.isFinite(leadLimit) && leadLimit >= 0 && leads.length >= leadLimit;

  const loadData = useCallback(() => {
    const _vertical = user?.dashboard_vertical || 'generic';
    const isAuto = _vertical === 'automotive';
    const promises = [
      api.get('/opportunities', { params: verSuspendidos ? { include_suspended: true } : {} }),
      api.get('/pipeline'), api.get('/team'),
      // paginacion-lista-leads: esta pantalla usa la lista COMPLETA de leads
      // (dropdown de oportunidad + contador de límite de licencia) -- limit=0
      // pide el modo "traer todo lo filtrado", NO la página por defecto.
      api.get('/leads', { params: { limit: 0 } }), api.get('/properties'), api.get('/lost-reasons'),
      api.get('/roulette/groups').catch(() => ({ data: [] })),
    ];
    if (isAuto) {
      promises.push(api.get('/car-models').catch(() => ({ data: [] })));
    }
    if (isDev) promises.push(api.get('/developments'));
    Promise.all(promises).then((res) => {
      const [o, p, t, l, pr, lr, rg] = res;
      const carModelsRes = isAuto ? res[7] : null;
      const d = isDev ? res[isAuto ? 8 : 7] : null;
      setOpps(o.data); setStages(p.data); setTeam(t.data);
      setLeads(l.data?.items || []); setProperties(pr.data); setLostReasons(lr.data);
      const rawGroups = Array.isArray(rg?.data) ? rg.data : (rg?.data?.items || []);
      setRouletteGroups(rawGroups.filter(g => g.is_active !== false));
      if (carModelsRes?.data) setCarModels(carModelsRes.data);
      if (d?.data) {
        setDevs(d.data);
        // Load all units from all developments
        Promise.all(d.data.map(dev => api.get(`/developments/${dev.id}`))).then(results => {
          const allUnits = results.flatMap(r => (r.data.units || []).map(u => ({ ...u, dev_name: r.data.name })));
          setDevUnits(allUnits);
        }).catch(() => {});
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [isDev, verSuspendidos]);

  useEffect(() => { loadData(); }, [loadData]);

  const getVendorName = (id) => team.find(m => m.id === id)?.name || 'Sin asignar';
  const getLeadName = (id) => leads.find(l => l.id === id)?.name || '';
  // WS-ACTIVIDADES-2: lead completo para mostrar Score IA en tarjetas.
  const getLead = (id) => leads.find(l => l.id === id) || null;
  const getPropTitle = (id) => properties.find(p => p.id === id)?.title || '';
  const formatPrice = (v, c) => `$${Number(v).toLocaleString('es-MX')} ${c}`;
  const getLostLabel = (id) => lostReasons.find(r => r.id === id)?.label || id;

  // WS-VERTICAL-OPPS-2 (pulido): normaliza el form para el vertical actual.
  // Limpia los campos que no aplican y precarga los que sí. Idempotente.
  const normalizeFormForVertical = (base) => {
    const isAuto = vertical === 'automotive';
    const isRE   = vertical === 'real_estate' || vertical === 'developments';
    const isEdu  = vertical === 'education';
    return {
      ...base,
      // Automotive.
      vehicle_type: isAuto ? (base.vehicle_type || '') : '',
      car_model_id: isAuto ? (base.car_model_id || '') : '',
      car_model: isAuto ? (base.car_model || '') : '',
      car_year: isAuto ? (base.car_year || '') : '',
      car_color: isAuto ? (base.car_color || '') : '',
      // Kilometraje solo aplica a Seminuevo.
      car_km: (isAuto && base.vehicle_type === 'seminuevo') ? (base.car_km || '') : '',
      // Real estate.
      property_id: isRE ? (base.property_id || '') : '',
      dev_unit_id: isRE ? (base.dev_unit_id || '') : '',
      dev_unit_label: isRE ? (base.dev_unit_label || '') : '',
      development_id: isRE ? (base.development_id || '') : '',
      // Education.
      programa: isEdu ? (base.programa || '') : '',
    };
  };

  const openNew = () => {
    if (isLeadLimitReached) {
      setLeadLimitDialogOpen(true);
      return;
    }
    setEditing(null);
    setForm(normalizeFormForVertical({ ...emptyOpp, stage_id: stages.find(s => !s.is_lost_stage)?.id || stages[0]?.id || '' }));
    setDialogOpen(true);
  };

  const openEdit = (opp) => {
    setEditing(opp.id);
    setOppAiActions([]);
    api.get(`/opportunities/${opp.id}/ai-actions`)
      .then(r => setOppAiActions(r.data?.ai_skill_actions || []))
      .catch(() => setOppAiActions([]));
    setForm(normalizeFormForVertical({
      title: opp.title || '', lead_id: opp.lead_id || '', property_id: opp.property_id || '',
      stage_id: opp.stage_id || '', value: opp.value || 0, currency: opp.currency || 'MXN',
      probability: opp.probability || 0, expected_close_date: opp.expected_close_date || '',
      assigned_to: opp.assigned_to || '', priority: opp.priority || 'medium',
      tags: opp.tags || [], custom_fields: opp.custom_fields || {},
      lost_reason: opp.lost_reason || '', lost_reason_detail: opp.lost_reason_detail || '',
      dev_unit_id: opp.dev_unit_id || '', dev_unit_label: opp.dev_unit_label || '',
      development_id: opp.development_id || '',
      // Vertical-aware fields (se preservan si vienen del doc).
      vehicle_type: opp.vehicle_type || '',
      car_model_id: opp.car_model_id || '',
      car_model: opp.car_model || '',
      car_year: opp.car_year || '',
      car_color: opp.car_color || '',
      car_km: opp.car_km || '',
      programa: opp.programa || '',
    }));
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/opportunities/${editing}`, form);
        toast.success('Oportunidad actualizada');
      } else {
        if (!String(form.lead_name || '').trim()) {
          toast.error('Nombre es obligatorio');
          return;
        }
        if (!String(form.lead_phone || '').trim() && !String(form.lead_email || '').trim() && !String(form.lead_bsuid || '').trim()) {
          toast.error('Indica al menos un medio de contacto: teléfono, correo o BSUID');
          return;
        }
        if (!String(form.lead_email || '').trim()) {
          toast.error('Email es obligatorio');
          return;
        }
        if (!String(form.lead_source || '').trim()) {
          toast.error('Canal/Origen es obligatorio');
          return;
        }

        const payload = {
          ...form,
          title: String(form.title || '').trim() || `Lead ${String(form.lead_name || '').trim()}`,
        };
        await api.post('/opportunities/lead-with-opportunity', payload);
        toast.success('Lead creado y oportunidad vinculada');
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error';
      if (e.response?.status === 403 && String(detail).toLowerCase().includes('limite')) {
        setLeadLimitDialogOpen(true);
      }
      toast.error(detail);
    }
  };

  const moveStage = async (opp, direction) => {
    const activeStages = stages.filter(s => !s.is_lost_stage && !s.is_cold_stage);
    const currentIdx = activeStages.findIndex(s => s.id === opp.stage_id);
    const newIdx = currentIdx + direction;
    if (newIdx < 0 || newIdx >= activeStages.length) return;
    const newStage = activeStages[newIdx];
    try {
      if (isDev && isQuoteStageName(newStage?.name)) {
        navigate(`/developments?quote_flow=1&opp_id=${opp.id}&target_stage_id=${newStage.id}`);
        toast.info('Para Desarrollos, la cotización se envía desde el Mapa 2D.');
        return;
      }
      await api.put(`/opportunities/${opp.id}`, { stage_id: newStage.id });
      toast.success(`Movido a ${newStage.name}`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openLostDialog = (opp) => {
    setLostTarget(opp);
    setLostForm({ lost_reason: '', lost_sub_reason: '', lost_reason_detail: '' });
    setLostDialogOpen(true);
  };

  const confirmLost = async () => {
    if (!lostForm.lost_reason) { toast.error('Selecciona un motivo'); return; }
    const lostStage = stages.find(s => s.is_lost_stage);
    if (!lostStage) { toast.error('No hay etapa de Perdido configurada'); return; }
    try {
      await api.put(`/opportunities/${lostTarget.id}`, {
        stage_id: lostStage.id, lost_reason: lostForm.lost_sub_reason || lostForm.lost_reason,
        lost_reason_detail: lostForm.lost_reason_detail, probability: 0
      });
      toast.success('Oportunidad marcada como perdida');
      setLostDialogOpen(false);
      loadData();
    } catch (e) { toast.error('Error'); }
  };

  const openColdDialog = (opp) => {
    setColdTarget(opp);
    setColdForm({ reactivation_date: '', return_stage_id: opp.stage_id, notes: '' });
    setColdDialogOpen(true);
  };

  const confirmCold = async () => {
    if (!coldForm.reactivation_date) { toast.error('Selecciona una fecha de reactivacion'); return; }
    try {
      await api.put(`/opportunities/${coldTarget.id}/freeze`, {
        reactivation_date: coldForm.reactivation_date,
        return_stage_id: coldForm.return_stage_id,
        notes: coldForm.notes
      });
      toast.success('Oportunidad congelada. Se reactivara automaticamente.');
      setColdDialogOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openOfferDialog = (opp) => {
    setOfferTarget(opp);
    setOfferForm({ amount: opp.value || 0, currency: opp.currency || 'MXN', offered_by: 'cliente', notes: '' });
    setOfferDialogOpen(true);
  };

  const submitOffer = async () => {
    try {
      await api.post(`/opportunities/${offerTarget.id}/offers`, offerForm);
      toast.success('Oferta registrada');
      setOfferDialogOpen(false);
      loadData();
    } catch (e) { toast.error('Error'); }
  };

  const openHistory = async (opp) => {
    setHistoryTarget(opp);
    setHistoryDialogOpen(true);
    try {
      const response = await api.get(`/opportunities/${opp.id}/quote-history`);
      setHistoryTarget((prev) => ({
        ...(prev || opp),
        quote_history_backend: response.data?.quotes || [],
        quotes_history_embedded: response.data?.embedded_quotes_history || [],
      }));
    } catch {
      // silently fallback to embedded data
    }
  };

  const updateOfferStatus = async (oppId, offerId, status) => {
    try {
      await api.put(`/opportunities/${oppId}/offers/${offerId}`, { status });
      toast.success(`Oferta ${status}`);
      loadData();
      // Refresh history target
      const updated = await api.get('/opportunities');
      const opp = updated.data.find(o => o.id === oppId);
      if (opp) setHistoryTarget(opp);
    } catch (e) { toast.error('Error'); }
  };

  const removeOpp = async (id) => {
    await eliminarOSuspender(`/opportunities/${id}`, {
      etiqueta: 'Oportunidad',
      confirmar: '¿Retirar esta oportunidad de la operación?',
      alTerminar: loadData,
    });
  };

  const getPriority = (p) => PRIORITIES.find(pr => pr.value === p) || PRIORITIES[1];

  // WS-ACTIVIDADES-2: actualización inline de calificación del lead desde la tarjeta de oportunidad.
  // dashboard-pipeline P0-D: 'prospecto' agregado — tras P0-A, las oportunidades creadas por
  // webhook/WhatsApp casi siempre cuelgan de un lead en 'prospecto', no 'nuevo'.
  const STATUS_OPTIONS = ['nuevo', 'prospecto', 'contactado', 'calificado', 'negociacion', 'ganado', 'perdido'];
  const updateLeadStatus = async (leadId, newStatus) => {
    if (!leadId) return;
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      toast.success(`Calificación actualizada a ${newStatus}`);
      loadData();
    } catch (e) {
      toast.error('No se pudo actualizar la calificación');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const totalValue = opps.reduce((sum, o) => sum + (o.value || 0), 0);
  const activeStages = stages.filter(s => !s.is_lost_stage && !s.is_cold_stage && !s.is_hidden);
  const lostStage = stages.find(s => s.is_lost_stage);
  const coldStage = stages.find(s => s.is_cold_stage);
  // WS-ACTIVIDADES-2: oportunidades filtradas client-side por fecha, producto, grupo y vendedor.
  const filteredOpps = opps.filter(o => matchOpp(o));
  function matchOpp(o, skip) {
    const dt = (o.created_at || o.expected_close_date || '').slice(0, 10);
    if (skip !== 'date') {
      if (oppDateFrom && dt && dt < oppDateFrom) return false;
      if (oppDateTo && dt && dt > oppDateTo) return false;
    }
    if (skip !== 'search' && oppSearch.trim()) {
      const ld = leads.find(l => l.id === o.lead_id);
      const term = oppSearch.trim().toLowerCase();
      const nameOk = (ld?.name || '').toLowerCase().includes(term);
      const phoneOk = (ld?.phone || '').includes(oppSearch.trim());
      if (!nameOk && !phoneOk) return false;
    }
    if (skip !== 'vendor' && oppVendorFilter !== 'all' && !isVendorRole && o.assigned_to !== oppVendorFilter) return false;
    if (skip !== 'team' && oppTeamFilter !== 'all' && !isVendorRole) {
      const g = rouletteGroups.find(x => x.id === oppTeamFilter);
      if (!memberIdsOfGroup(g).includes(o.assigned_to)) return false;
    }
    if (skip !== 'product' && showProductFilter && oppProductFilter !== 'all') {
      const ld = leads.find(l => l.id === o.lead_id);
      if (!ld || ld.product_id !== oppProductFilter) return false;
    }
    if (skip !== 'dev' && showGroupFilter && oppGroupFilter !== 'all') {
      const ld = leads.find(l => l.id === o.lead_id);
      const inDev = ld && (ld.developments_of_interest || []).includes(oppGroupFilter);
      if (!inDev) return false;
    }
    if (skip !== 'operation' && showOperationFilter && oppOperationFilter !== 'all') {
      const ld = leads.find(l => l.id === o.lead_id);
      if (!ld || ld.operation_type !== oppOperationFilter) return false;
    }
    return true;
  }
  const facetVendorIds = idsOf(opps.filter(o => matchOpp(o, 'vendor')), 'assigned_to');
  const facetProductIds = idsOf(opps.filter(o => matchOpp(o, 'product')), (o) => leads.find(l => l.id === o.lead_id)?.product_id);
  const facetDevIds = idsOf(opps.filter(o => matchOpp(o, 'dev')), (o) => leads.find(l => l.id === o.lead_id)?.developments_of_interest);
  const facetTeamVendorIds = new Set(idsOf(opps.filter(o => matchOpp(o, 'team')), 'assigned_to'));
  const facetTeamIds = rouletteGroups.filter(g => memberIdsOfGroup(g).some(id => facetTeamVendorIds.has(id))).map(g => g.id);
  const lostOpps = lostStage ? filteredOpps.filter(o => o.stage_id === lostStage.id) : [];
  const coldOpps = coldStage ? filteredOpps.filter(o => o.stage_id === coldStage.id) : [];
  const selectedReasonGroup = lostReasons.find(r => r.id === lostForm.lost_reason);

  return (
    <div data-testid="opportunities-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Oportunidades</h2>
            <HelpTip title="Pipeline" content="Prospecto es la primera etapa automática. Desde aquí gestionas avances, pérdidas, fríos y ganados para disparar lógicas y cierres." />
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="opp-admin-sections">
              <button
                type="button"
                onClick={() => setSeccion('pipeline')}
                className={`px-3 py-1.5 rounded-lg text-xs border ${seccion === 'pipeline' ? 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300' : 'border-white/10 text-zinc-300'}`}
                data-testid="opp-section-pipeline"
              >
                Pipeline
              </button>
              <button
                type="button"
                onClick={() => setSeccion('reglas')}
                className={`px-3 py-1.5 rounded-lg text-xs border ${seccion === 'reglas' ? 'border-orange-500/40 bg-orange-500/15 text-orange-300' : 'border-white/10 text-zinc-300'}`}
                data-testid="opp-section-reglas"
              >
                Reglas
              </button>
            </div>
          )}
          <div className="flex items-center gap-4 mt-1">
            <span className="text-muted-foreground text-sm">{opps.length} oportunidades</span>
            <span className="text-sm gold-accent font-semibold">{formatPrice(totalValue, 'MXN')} en pipeline</span>
            {isLeadLimitReached && <span className="text-[11px] text-red-300" data-testid="opportunities-lead-limit-warning">Límite de leads alcanzado</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setBellOpen(true)} variant="outline" className="border-amber-500/30 hover:bg-amber-500/10 text-amber-300 relative" data-testid="opp-bell-btn" title="Mis actividades y Coach IA">
            <Bell className={`w-4 h-4 ${bellPending > 0 ? 'animate-pulse' : ''}`} />
            {bellPending > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-card shadow shadow-red-500/30"
                style={{ animation: 'pulse 2s ease-in-out infinite' }}
                data-testid="opp-bell-badge"
              >
                {bellPending > 99 ? '99+' : bellPending}
              </span>
            )}
          </Button>
          <Button onClick={openNew} className={`font-semibold ${isLeadLimitReached ? 'bg-red-600/20 border border-red-500/50 text-red-200 hover:bg-red-600/30' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20'}`} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" />Crear Lead
          </Button>
        </div>
      </div>

      {seccion === 'reglas' ? (
        <div data-testid="opp-rules-panel">
          <p className="text-xs text-muted-foreground mb-4">
            Cuando una oportunidad cambia de etapa, estas reglas avisan, reasignan o mueven el trato. Solo el administrador las edita. Quién recibe el lead (ruleta) sigue en Team Config.
          </p>
          <RulesPage />
        </div>
      ) : (
      <>
      {/* WS-ACTIVIDADES-2: filtros del pipeline (fecha + producto + grupo + vendedor). */}
      {/* Opción C (unificado con LeadsPage): 3 filas. Fila 1 = Buscar + Producto/Desarrollo,
          Fila 2 = Vendedor + Grupo + Rango, Fila 3 = "+ Más filtros" (Tipo de Operación) + Ver archivados. */}
      <div className="mb-3 flex flex-wrap items-end gap-3" data-testid="opp-filters">
        <FilterField label="Buscar" width="200px">
          <input
            type="text"
            value={oppSearch}
            onChange={(e) => setOppSearch(e.target.value)}
            placeholder="Nombre o teléfono"
            className="w-full h-full text-xs border border-white/10 rounded-md px-3 py-1 bg-background text-foreground"
            data-testid="opp-filter-search"
          />
        </FilterField>
        {!isVendorRole && showProductFilter && (
          <FilterField label="Producto">
            <Select value={oppProductFilter} onValueChange={setOppProductFilter}>
              <SelectTrigger className="h-full w-full bg-black/20 border-white/10 text-xs" data-testid="opp-filter-product"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {byFacet(properties || [], facetProductIds, oppProductFilter).map(p => <SelectItem key={p.id} value={p.id}>{p.name || p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}
        {!isVendorRole && showGroupFilter && (
          <FilterField label="Desarrollo">
            <Select value={oppGroupFilter} onValueChange={setOppGroupFilter}>
              <SelectTrigger className="h-full w-full bg-black/20 border-white/10 text-xs" data-testid="opp-filter-group"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {(devs || []).map(d => <SelectItem key={d.id} value={d.id}>{d.name || d.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        {!isVendorRole && rouletteGroups.length > 0 && (
          <FilterField label="Grupo">
            <Select
              value={oppTeamFilter}
              onValueChange={(v) => {
                setOppTeamFilter(v);
                if (v !== 'all' && oppVendorFilter !== 'all') {
                  const g = rouletteGroups.find(x => x.id === v);
                  if (!memberIdsOfGroup(g).includes(oppVendorFilter)) setOppVendorFilter('all');
                }
              }}
            >
              <SelectTrigger className="h-full w-full bg-black/20 border-white/10 text-xs" data-testid="opp-filter-team">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{oppVendorFilter !== 'all' ? 'Todos los de este vendedor' : 'Todos los grupos'}</SelectItem>
                {byFacet(groupsVisibleForVendor(rouletteGroups, oppVendorFilter), facetTeamIds, oppTeamFilter).map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        )}
        {!isVendorRole && (
          <FilterField label="Vendedor">
            <Select
              value={oppVendorFilter}
              onValueChange={(v) => {
                setOppVendorFilter(v);
                if (v !== 'all' && oppTeamFilter !== 'all') {
                  const g = rouletteGroups.find(x => x.id === oppTeamFilter);
                  if (!memberIdsOfGroup(g).includes(v)) setOppTeamFilter('all');
                }
              }}
            >
              <SelectTrigger className="h-full w-full bg-black/20 border-white/10 text-xs" data-testid="opp-filter-vendor"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{oppTeamFilter !== 'all' ? 'Todos los del grupo' : 'Todos'}</SelectItem>
                {byFacet(vendorsVisibleForGroup(team, rouletteGroups, oppTeamFilter), facetVendorIds, oppVendorFilter).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}
        <FilterField label="Rango" width="240px">
          <div className="flex items-center gap-1 h-full">
            <Input type="date" value={oppDateFrom} onChange={(e) => setOppDateFrom(e.target.value)} className="h-full w-full bg-black/20 border-white/10 text-xs px-1.5" data-testid="opp-filter-date-from" />
            <Input type="date" value={oppDateTo} onChange={(e) => setOppDateTo(e.target.value)} className="h-full w-full bg-black/20 border-white/10 text-xs px-1.5" data-testid="opp-filter-date-to" />
          </div>
        </FilterField>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        {/* tipo-operacion-renta-venta: filtro client-side, mismo patron que oppGroupFilter --
            join contra `leads` (ya cargado con limit=0), sin tocar routes/opportunities.py. */}
        {!isVendorRole && showOperationFilter && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="h-8 px-3 text-xs flex items-center gap-2 border border-white/10 rounded-md bg-black/20 text-foreground"
                data-testid="opp-filter-more-toggle"
              >
                + Más filtros
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="start">
              <FilterField label="Tipo de Operación" width="100%">
                <Select value={oppOperationFilter} onValueChange={setOppOperationFilter}>
                  <SelectTrigger className="h-full w-full bg-black/20 border-white/10 text-xs" data-testid="opp-filter-operation"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="renta">Renta</SelectItem>
                    <SelectItem value="venta">Venta</SelectItem>
                  </SelectContent>
                </Select>
              </FilterField>
            </PopoverContent>
          </Popover>
        )}
        <div className="self-end pb-1">
          <VerSuspendidos
            checked={verSuspendidos}
            onChange={setVerSuspendidos}
            testid="opp-ver-suspendidos"
            label="Ver archivados"
            title="Oportunidades archivadas -- se muestran de solo lectura, sin edición ni acciones."
          />
        </div>
        <div className="text-[11px] text-muted-foreground ml-auto self-end pb-1.5" data-testid="opp-filter-count">
          {filteredOpps.length}/{opps.length} oportunidades
        </div>
      </div>

      {user?.role === 'admin' && (
        <Card className="mb-6 bg-card/40 border-white/10" data-testid="pipeline-stage-admin-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Administrador de etapas</h3>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsStageAdminCollapsed(prev => !prev)}
                  data-testid="toggle-stage-admin-section-button"
                >
                  {isStageAdminCollapsed ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronUp className="w-4 h-4 mr-1" />}
                  {isStageAdminCollapsed ? 'Expandir' : 'Contraer'}
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  const name = window.prompt('Nombre de nueva etapa');
                  if (!name) return;
                  await api.post('/pipeline', {
                    name,
                    color: '#6366f1',
                    order: stages.length + 1,
                    is_won_stage: false,
                    is_lost_stage: false,
                    is_cold_stage: false,
                    is_apartado_stage: false,
                    release_inventory: false,
                    auto_quote_enabled: false,
                    auto_quote_channels: ['email', 'whatsapp']
                  });
                  loadData();
                }} data-testid="add-stage-button">Agregar etapa</Button>
              </div>
            </div>
            {!isStageAdminCollapsed ? (
              <div className="space-y-2" data-testid="stage-admin-list">
                {stages.filter(s => !s.is_won_stage && !s.is_lost_stage && !s.is_cold_stage).sort((a,b)=>(a.order||0)-(b.order||0)).map((s, idx, arr) => (
                  <div key={s.id} className="rounded-lg border border-white/10 p-2 flex flex-wrap items-center gap-2" data-testid={`stage-admin-row-${s.id}`}>
                    <Input value={s.name} onChange={e => setStages(prev => prev.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))} className="bg-black/20 border-white/10 h-8 min-w-[180px]" data-testid={`stage-name-${s.id}`} />
                    <Button size="sm" variant="outline" disabled={idx===0} onClick={async ()=>{ await api.put(`/pipeline/${s.id}`, { order: (arr[idx-1]?.order||1) }); await api.put(`/pipeline/${arr[idx-1]?.id}`, { order: s.order }); loadData(); }} data-testid={`stage-up-${s.id}`}>↑</Button>
                    <Button size="sm" variant="outline" disabled={idx===arr.length-1} onClick={async ()=>{ await api.put(`/pipeline/${s.id}`, { order: (arr[idx+1]?.order||1) }); await api.put(`/pipeline/${arr[idx+1]?.id}`, { order: s.order }); loadData(); }} data-testid={`stage-down-${s.id}`}>↓</Button>
                    <Button size="sm" variant={s.is_hidden ? 'default' : 'outline'} onClick={async ()=>{ await api.put(`/pipeline/${s.id}`, { is_hidden: !s.is_hidden }); loadData(); }} data-testid={`stage-hide-toggle-${s.id}`}>{s.is_hidden ? 'Mostrar' : 'Ocultar'}</Button>
                    <Button
                      size="sm"
                      variant={s.is_apartado_stage ? 'default' : 'outline'}
                      onClick={() => setStages(prev => prev.map(x => x.id === s.id ? { ...x, is_apartado_stage: !x.is_apartado_stage } : x))}
                      data-testid={`stage-apartado-toggle-${s.id}`}
                    >
                      Apartado
                    </Button>
                    <Button
                      size="sm"
                      variant={s.release_inventory ? 'default' : 'outline'}
                      onClick={() => setStages(prev => prev.map(x => x.id === s.id ? { ...x, release_inventory: !x.release_inventory } : x))}
                      data-testid={`stage-release-toggle-${s.id}`}
                    >
                      Libera inventario
                    </Button>
                    <Button
                      size="sm"
                      variant={s.auto_quote_enabled ? 'default' : 'outline'}
                      onClick={() => setStages(prev => prev.map(x => x.id === s.id ? { ...x, auto_quote_enabled: !x.auto_quote_enabled } : x))}
                      data-testid={`stage-auto-quote-toggle-${s.id}`}
                    >
                      Cotización Auto
                    </Button>
                    <Button
                      size="sm"
                      variant={Array.isArray(s.auto_quote_channels) && s.auto_quote_channels.includes('whatsapp') ? 'default' : 'outline'}
                      onClick={() => {
                        const current = Array.isArray(s.auto_quote_channels) ? s.auto_quote_channels : ['email', 'whatsapp'];
                        const next = current.includes('whatsapp') ? current.filter(c => c !== 'whatsapp') : [...current, 'whatsapp'];
                        setStages(prev => prev.map(x => x.id === s.id ? { ...x, auto_quote_channels: next } : x));
                      }}
                      data-testid={`stage-quote-channel-wa-${s.id}`}
                    >
                      WA
                    </Button>
                    <Button
                      size="sm"
                      variant={Array.isArray(s.auto_quote_channels) && s.auto_quote_channels.includes('email') ? 'default' : 'outline'}
                      onClick={() => {
                        const current = Array.isArray(s.auto_quote_channels) ? s.auto_quote_channels : ['email', 'whatsapp'];
                        const next = current.includes('email') ? current.filter(c => c !== 'email') : [...current, 'email'];
                        setStages(prev => prev.map(x => x.id === s.id ? { ...x, auto_quote_channels: next } : x));
                      }}
                      data-testid={`stage-quote-channel-email-${s.id}`}
                    >
                      Email
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSeccion('reglas')} data-testid={`stage-rules-button-${s.id}`}>Reglas</Button>
                    <Button size="sm" onClick={async ()=>{
                      const channels = Array.isArray(s.auto_quote_channels) && s.auto_quote_channels.length > 0 ? s.auto_quote_channels : ['email', 'whatsapp'];
                      await api.put(`/pipeline/${s.id}`, {
                        name: s.name,
                        order: s.order,
                        rules: s.rules || {},
                        is_apartado_stage: !!s.is_apartado_stage,
                        release_inventory: !!s.release_inventory,
                        auto_quote_enabled: !!s.auto_quote_enabled,
                        auto_quote_channels: channels,
                      });
                      toast.success('Etapa guardada');
                    }} data-testid={`stage-save-${s.id}`}>Guardar</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-white/10 px-3 py-2" data-testid="stage-admin-collapsed-hint">
                <p className="text-xs text-muted-foreground">Sección contraída. Usa “Expandir” para administrar etapas.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Kanban Board - Active Stages */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
        {activeStages.map(stage => {
          const stageOpps = filteredOpps.filter(o => o.stage_id === stage.id);
          const stageValue = stageOpps.reduce((s, o) => s + (o.value || 0), 0);
          return (
            <div key={stage.id} className="flex-shrink-0 w-72" data-testid={`stage-column-${stage.id}`}>
              <div className="sticky top-0 z-10 mb-3 p-3 rounded-xl" style={{ backgroundColor: `${stage.color}15`, borderLeft: `3px solid ${stage.color}` }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{stage.name}</span>
                  <Badge variant="outline" className="text-[10px] border-white/10">{stageOpps.length}</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{formatPrice(stageValue, 'MXN')}</p>
              </div>
              <div className="space-y-3">
                {stageOpps.map(opp => {
                  const priority = getPriority(opp.priority);
                  const stageIdx = activeStages.findIndex(s => s.id === opp.stage_id);
                  const leadAct = leadActivities[opp.lead_id];
                  const hasPending = !!(leadAct && leadAct.count_pending > 0);
                  // Fase 4.5 + "Ver archivados" (Opción C): una oportunidad archivada/suspendida
                  // es SOLO LECTURA -- no drag, no editar, no agregar. Reversión sigue siendo
                  // nivel Dios (destruction_policy), la UI no debe insinuar que es reversible fácil.
                  const readOnly = !!opp.suspended;
                  return (
                    <Card key={opp.id} className={`bg-card/60 backdrop-blur-md border-white/5 hover:border-white/15 group relative ${readOnly ? 'opacity-70' : 'cursor-pointer'} ${hasPending ? 'border-amber-500/40 shadow-amber-500/10 shadow-lg' : ''} ${isResurrectedOpp(opp, getLead(opp.lead_id)) ? 'opp-resurrected' : ''}`} style={{ transition: 'border-color 0.2s ease' }} data-testid={`opp-card-${opp.id}`}>
                      {isResurrectedOpp(opp, getLead(opp.lead_id)) && (
                        <span
                          className="absolute -top-1.5 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-600 text-white shadow"
                          data-testid={`opp-resurrected-badge-${opp.id}`}
                        >
                          Resucitado
                        </span>
                      )}
                      {hasPending && (
                        <span
                          className="absolute -top-1.5 -right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500 text-black shadow shadow-amber-500/30"
                          style={{ animation: 'pulse 2.5s ease-in-out infinite' }}
                          data-testid={`opp-card-pending-badge-${opp.id}`}
                        >
                          <Bell className="w-2.5 h-2.5" />{leadAct.count_pending}
                        </span>
                      )}
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium text-white leading-tight flex-1 pr-2">{opp.title}</h4>
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${priority.color}`}>{priority.label}</span>
                        </div>
                        <p className="text-base font-bold gold-accent mb-2">{formatPrice(opp.value, opp.currency)}</p>
                        <div className="space-y-1 mb-3">
                          {opp.lead_id && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" />{getLeadName(opp.lead_id)}</p>}
                          {opp.property_id && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Target className="w-3 h-3" />{getPropTitle(opp.property_id)}</p>}
                          {opp.dev_unit_label && <p className="text-[11px] text-violet-400 flex items-center gap-1"><Building2 className="w-3 h-3" />{opp.dev_unit_label}</p>}
                          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" />{getVendorName(opp.assigned_to)}</p>
                          {opp.expected_close_date && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" />{opp.expected_close_date}</p>}
                          {/* WS-VERTICAL-OPPS: chip nativo por vertical (auto / unidad / propiedad / programa). */}
                          {(() => {
                            const ld = getLead(opp.lead_id);
                            if (!ld && !opp.dev_unit_id && !opp.property_id) return null;
                            let label = null;
                            let cls = 'bg-blue-500/10 border-blue-500/25 text-blue-300';
                            if (vertical === 'automotive') {
                              const v = ld?.vehicle_type || opp.vehicle_type;
                              const model = ld?.car_model || ld?.product_name || opp.car_model;
                              if (v || model) {
                                label = `${v ? v.toUpperCase() + ' · ' : ''}${model || ''}`.trim().replace(/·\s*$/, '');
                                cls = 'bg-amber-500/10 border-amber-500/25 text-amber-300';
                              }
                            } else if (vertical === 'real_estate' || vertical === 'developments') {
                              if (opp.dev_unit_id) {
                                const u = devUnits?.find(x => x.id === opp.dev_unit_id);
                                if (u) { label = `Unidad ${u.label || u.id} · ${u.dev_name || ''}`.trim(); cls = 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'; }
                              } else if (opp.property_id) {
                                const p = (properties || []).find(x => x.id === opp.property_id);
                                if (p) { label = `Propiedad: ${p.title || p.name}`; }
                              }
                            } else if (vertical === 'education') {
                              const prog = ld?.programa || ld?.product_name;
                              if (prog) { label = `Programa: ${prog}`; cls = 'bg-purple-500/10 border-purple-500/25 text-purple-300'; }
                            }
                            if (!label) return null;
                            return (
                              <div className="flex flex-col gap-0.5" data-testid={`opp-vertical-info-${opp.id}`}>
                                <p className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] self-start ${cls}`} data-testid={`opp-vertical-chip-${opp.id}`}>{label}</p>
                                {vertical === 'automotive' && (opp.car_model || opp.car_year || opp.car_color) && (
                                  <p className="text-[10px] text-muted-foreground" data-testid={`opp-vertical-detail-auto-${opp.id}`}>
                                    {opp.car_model || ''}{opp.car_year ? ` · ${opp.car_year}` : ''}{opp.car_color ? ` · ${opp.car_color}` : ''}{opp.vehicle_type === 'seminuevo' && opp.car_km ? ` · ${opp.car_km} km` : ''}
                                  </p>
                                )}
                                {vertical === 'education' && opp.programa && (
                                  <p className="text-[10px] text-muted-foreground" data-testid={`opp-vertical-detail-edu-${opp.id}`}>Programa: {opp.programa}</p>
                                )}
                              </div>
                            );
                          })()}
                          {opp.lead_id && (() => {
                            const ld = getLead(opp.lead_id);
                            if (!ld) return null;
                            const score = ld.ai_score ?? ld.score ?? null;
                            const qual = ld.calificacion ?? ld.qualification ?? ld.status ?? null;
                            if (score == null && !qual) return null;
                            const scoreColor = score == null ? 'text-zinc-300 bg-zinc-500/15 border-zinc-500/30' :
                              score >= 80 ? 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30' :
                              score >= 60 ? 'text-sky-300 bg-sky-500/15 border-sky-500/30' :
                              score >= 40 ? 'text-amber-300 bg-amber-500/15 border-amber-500/30' :
                              'text-rose-300 bg-rose-500/15 border-rose-500/30';
                            return (
                              <div className="flex items-center gap-1.5 flex-wrap" data-testid={`opp-lead-score-${opp.id}`}>
                                {score != null && (
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-semibold ${scoreColor}`}>
                                    <Sparkles className="w-2.5 h-2.5" />Score IA {score}
                                  </span>
                                )}
                                {qual && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-white/10 bg-white/5 text-[9px] text-muted-foreground">
                                    {qual}
                                  </span>
                                )}
                                {/* WS-ACTIVIDADES-2: selector inline de calificación */}
                                <Select value={ld.status || 'nuevo'} onValueChange={(v) => updateLeadStatus(ld.id, v)} disabled={readOnly}>
                                  <SelectTrigger onClick={(e) => e.stopPropagation()} className="h-5 px-1.5 py-0 text-[9px] bg-white/5 border-white/10 rounded-full w-auto disabled:opacity-50" data-testid={`opp-lead-status-select-${opp.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })()}
                          {opp.lead_id && (() => {
                            const ld = getLead(opp.lead_id);
                            if (!ld) return null;
                            const phone = ld.phone || '';
                            const bsuid = ld.bsuid || '';
                            const email = ld.email || '';
                            const source = ld.source || ld.campaign_name || '';
                            if (!phone && !bsuid && !email && !source) return null;
                            return (
                              <div className="flex flex-col gap-0.5 mt-1" data-testid={`opp-lead-meta-${opp.id}`}>
                                {(phone || bsuid) && <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{phone || 'WhatsApp sin número'}</p>}
                                {email && <p className="text-[10px] text-muted-foreground flex items-center gap-1 truncate"><Mail className="w-2.5 h-2.5" />{email}</p>}
                                {source && <p className="text-[10px] text-muted-foreground italic">via {source}</p>}
                              </div>
                            );
                          })()}
                          {hasPending && leadAct.last_type && (
                            <p className="text-[10px] text-amber-300 flex items-center gap-1 mt-1" data-testid={`opp-lead-last-activity-${opp.id}`}>
                              <ClipboardList className="w-2.5 h-2.5" />Última: {leadAct.last_type} · {(leadAct.last_at || '').slice(0, 10)}
                            </p>
                          )}
                        </div>
                        <div className="w-full h-1.5 bg-black/30 rounded-full mb-3">
                          <div className="h-full rounded-full" style={{ width: `${opp.probability}%`, backgroundColor: stage.color, transition: 'width 0.3s ease' }} />
                        </div>
                        {readOnly ? (
                          <div className="flex items-center gap-1.5 text-[10px] text-amber-300/80" data-testid={`opp-readonly-badge-${opp.id}`}>
                            <Archive className="w-3 h-3" />Archivada — solo lectura
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveStage(opp, -1); }} disabled={stageIdx === 0} className="h-6 w-6 p-0 text-muted-foreground hover:text-white disabled:opacity-20" data-testid={`opp-prev-${opp.id}`}><ChevronLeft className="w-3.5 h-3.5" /></Button>
                            <span className="text-[10px] text-muted-foreground">{opp.probability}%</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(opp); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-[#A5D62C] opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-edit-${opp.id}`}><Pencil className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openOfferDialog(opp); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-amber-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-offer-${opp.id}`} title="Registrar oferta"><DollarSign className="w-3 h-3" /></Button>
                              {(opp.negotiation_history?.length > 0 || opp.quotes_history?.length > 0) && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openHistory(opp); }} className="h-6 w-6 p-0 text-amber-400/60 hover:text-amber-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-history-${opp.id}`} title="Historial"><History className="w-3 h-3" /></Button>}
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setActivityContext({ leadId: opp.lead_id || null, opportunityId: opp.id, leadName: getLeadName(opp.lead_id) || opp.title || '' }); setActivityDialogOpen(true); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-emerald-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-activity-${opp.id}`} title="Registrar actividad"><ClipboardList className="w-3 h-3" /></Button>
                              {opp.lead_id && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setLeadActivitiesView({ open: true, leadId: opp.lead_id, leadName: getLeadName(opp.lead_id) || opp.title || '' }); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-amber-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-view-activities-${opp.id}`} title="Ver actividades"><Bell className="w-3 h-3" /></Button>}
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openColdDialog(opp); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-cold-${opp.id}`} title="Congelar"><Snowflake className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openLostDialog(opp); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-lost-${opp.id}`}><XCircle className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeOpp(opp.id); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100" style={{ transition: 'opacity 0.2s ease' }} data-testid={`opp-delete-${opp.id}`}><Trash2 className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveStage(opp, 1); }} disabled={stageIdx === activeStages.length - 1} className="h-6 w-6 p-0 text-muted-foreground hover:text-white disabled:opacity-20" data-testid={`opp-next-${opp.id}`}><ChevronRight className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                {stageOpps.length === 0 && (
                  <div className="border border-dashed border-white/5 rounded-xl p-4 text-center">
                    <p className="text-[11px] text-muted-foreground/50">Sin oportunidades</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lost Opportunities Section */}
      {lostStage && lostOpps.length > 0 && (
        <div className="mt-6" data-testid="lost-opportunities-section">
          <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-red-500/[0.06] border border-red-500/10">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-semibold text-red-400">Oportunidades Perdidas</span>
            <Badge variant="outline" className="text-[10px] border-red-500/20 text-red-400">{lostOpps.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {lostOpps.map(opp => (
              <Card key={opp.id} className={`bg-card/30 border-red-500/10 hover:border-red-500/20 ${isResurrectedOpp(opp, getLead(opp.lead_id)) ? 'opp-resurrected' : ''}`} data-testid={`lost-opp-${opp.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-white/60">{opp.title}</h4>
                    <Button variant="ghost" size="sm" onClick={() => removeOpp(opp.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <p className="text-sm font-bold text-red-400/60 line-through mb-2">{formatPrice(opp.value, opp.currency)}</p>
                  {opp.lost_reason && (
                    <div className="p-2 rounded-lg bg-red-500/[0.05] border border-red-500/10 mb-2">
                      <p className="text-[10px] text-red-400 font-semibold uppercase tracking-wider mb-0.5">Motivo</p>
                      <p className="text-xs text-white/60">{getLostLabel(opp.lost_reason)}</p>
                      {opp.lost_reason_detail && <p className="text-[11px] text-muted-foreground mt-1 italic">&ldquo;{opp.lost_reason_detail}&rdquo;</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{getLeadName(opp.lead_id) || 'Sin lead'}</span>
                    <span>&middot;</span>
                    <span>{getVendorName(opp.assigned_to)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Cold Opportunities Section */}
      {coldStage && coldOpps.length > 0 && (
        <div className="mt-6" data-testid="cold-opportunities-section">
          <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-blue-500/[0.06] border border-blue-500/10">
            <Snowflake className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400">Oportunidades Frias</span>
            <Badge variant="outline" className="text-[10px] border-blue-500/20 text-blue-400">{coldOpps.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {coldOpps.map(opp => (
              <Card key={opp.id} className="bg-card/30 border-blue-500/10 hover:border-blue-500/20" data-testid={`cold-opp-${opp.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-white/60">{opp.title}</h4>
                    <Button variant="ghost" size="sm" onClick={() => removeOpp(opp.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <p className="text-sm font-bold text-blue-400/80 mb-2">{formatPrice(opp.value, opp.currency)}</p>
                  {opp.cold_reactivation_date && (
                    <div className="p-2 rounded-lg bg-blue-500/[0.05] border border-blue-500/10 mb-2">
                      <p className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider mb-0.5">Reactivacion</p>
                      <p className="text-xs text-white/60 flex items-center gap-1"><Calendar className="w-3 h-3" />{opp.cold_reactivation_date}</p>
                      {opp.cold_notes && <p className="text-[11px] text-muted-foreground mt-1 italic">&ldquo;{opp.cold_notes}&rdquo;</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{getVendorName(opp.assigned_to)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'Editar Lead/Oportunidad' : 'Crear Lead'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editing ? 'Modifica los datos' : 'Formulario unificado de Lead + Oportunidad'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div className="space-y-3" data-testid="lead-required-fields-section">
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2" data-testid="lead-required-fields-note">
                  <p className="text-[11px] text-emerald-300">Solo obligatorios al crear: Nombre, Teléfono, Email y Canal/Origen.</p>
                </div>
                <div className="grid grid-cols-2 gap-3" data-testid="lead-fields-grid">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Nombre Lead *<HelpTip title="Nombre" content="Nombre completo del lead. Campo obligatorio en esta etapa." /></Label>
                    <Input value={form.lead_name || ''} onChange={e => setForm({...form, lead_name: e.target.value})} data-testid="lead-name-input" className="bg-black/20 border-white/10" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Teléfono Lead *<HelpTip title="Teléfono" content="Número principal para seguimiento y WhatsApp." /></Label>
                    <PhoneInput data-testid="lead-phone-input" value={form.lead_phone || ''} onChange={v => setForm({...form, lead_phone: v})} />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Email Lead *<HelpTip title="Email" content="Correo requerido para envío de cotizaciones y trazabilidad." /></Label>
                    <Input value={form.lead_email || ''} onChange={e => setForm({...form, lead_email: e.target.value})} data-testid="lead-email-input" className="bg-black/20 border-white/10" />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">Canal / Origen *<HelpTip title="Canal/Origen" content="Crítico para medir campañas y atribución comercial." /></Label>
                    <Select value={form.lead_source || 'manual'} onValueChange={v => setForm({...form, lead_source: v})}>
                      <SelectTrigger className="bg-black/20 border-white/10" data-testid="lead-source-input"><SelectValue placeholder="Seleccionar origen" /></SelectTrigger>
                      <SelectContent>{LEAD_SOURCES.map(source => <SelectItem key={source} value={source}>{source}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2"><Label>Titulo</Label><Input data-testid="opp-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Venta Casa Polanco" className="bg-black/20 border-white/10 focus:border-primary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Valor</Label><Input data-testid="opp-value" type="number" value={form.value} onChange={e => setForm({...form, value: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 focus:border-primary" /></div>
              <div className="space-y-2">
                <Label>Probabilidad %</Label>
                <Input data-testid="opp-probability" type="number" min={0} max={100} value={form.probability} onChange={e => setForm({...form, probability: parseInt(e.target.value) || 0})} className="bg-black/20 border-white/10 focus:border-primary" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Etapa</Label>
                <Select value={form.stage_id} onValueChange={v => setForm({...form, stage_id: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-stage"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{stages.filter(s => !s.is_lost_stage && !s.is_hidden && (editing || !isDev || !isQuoteStageName(s.name))).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Lead asociado</Label>
                <Select value={form.lead_id || 'none'} onValueChange={v => setForm({...form, lead_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-lead"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ninguno</SelectItem>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {/* Campo de inventario por vertical: la vertical PROPONE, allowed_modules VETA
                  (inventoryModule). 'developments' → SIN campo acá (su Unidad va en el bloque
                  isDev de abajo, decisión LOCKED). Sin módulo aplicable → SIN campo: no hay
                  else "Propiedad" cajón de sastre. */}
              {inventoryModule === 'car_dealer' ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><span className="text-amber-400">🚗</span>Tipo de vehículo</Label>
                  <Select value={form.vehicle_type || 'none'} onValueChange={v => {
                    const newType = v === 'none' ? '' : v;
                    setForm({
                      ...form,
                      vehicle_type: newType,
                      // Limpia km si ya no es seminuevo.
                      car_km: newType === 'seminuevo' ? (form.car_km || '') : '',
                    });
                  }}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-vehicle-type"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin definir</SelectItem>
                      <SelectItem value="nuevo">Nuevo</SelectItem>
                      <SelectItem value="seminuevo">Seminuevo</SelectItem>
                      <SelectItem value="servicio">Servicio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : inventoryModule === 'products' ? (
                <div className="space-y-2">
                  <Label>{vertical === 'education' ? 'Programa de interés' : 'Producto de interés'}</Label>
                  <Input data-testid="opp-programa" value={form.programa || ''} onChange={e => setForm({...form, programa: e.target.value})} placeholder={vertical === 'education' ? 'Maestría en IA, Bachillerato, etc.' : 'Producto o servicio de interés'} className="bg-black/20 border-white/10" />
                </div>
              ) : inventoryModule === 'properties' ? (
                <div className="space-y-2">
                  <Label>Propiedad</Label>
                  <Select value={form.property_id || 'none'} onValueChange={v => setForm({...form, property_id: v === 'none' ? '' : v})}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-property"><SelectValue placeholder="Opcional" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Ninguna</SelectItem>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
            {/* Bloque automotive: modelo + año + color/km. Mismo gate que "Tipo de vehículo". */}
            {inventoryModule === 'car_dealer' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Modelo de auto</Label>
                  <Select value={form.car_model_id || 'none'} onValueChange={v => {
                    if (v === 'none') { setForm({...form, car_model_id: '', car_model: ''}); }
                    else {
                      const m = carModels.find(x => x.id === v);
                      setForm({...form, car_model_id: v, car_model: m ? `${m.brand || ''} ${m.name}`.trim() : ''});
                    }
                  }}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-car-model"><SelectValue placeholder={carModels.length ? 'Seleccionar modelo' : 'Aun no hay modelos cargados'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin definir</SelectItem>
                      {carModels.length === 0 && (
                        <SelectItem value="__empty__" disabled className="text-muted-foreground text-xs">Carga modelos desde Catalogo</SelectItem>
                      )}
                      {carModels.map(m => <SelectItem key={m.id} value={m.id}>{(m.brand ? m.brand + ' ' : '') + m.name + (m.year ? ' · ' + m.year : '')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Año</Label>
                  <Input data-testid="opp-car-year" type="number" value={form.car_year || ''} onChange={e => setForm({...form, car_year: e.target.value})} placeholder="2024" className="bg-black/20 border-white/10" />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Input data-testid="opp-car-color" value={form.car_color || ''} onChange={e => setForm({...form, car_color: e.target.value})} placeholder="Blanco perla" className="bg-black/20 border-white/10" />
                </div>
                {form.vehicle_type === 'seminuevo' && (
                  <div className="space-y-2">
                    <Label>Kilometraje</Label>
                    <Input data-testid="opp-car-km" type="number" value={form.car_km || ''} onChange={e => setForm({...form, car_km: e.target.value})} placeholder="35000" className="bg-black/20 border-white/10" />
                  </div>
                )}
              </div>
            )}
            {isDev && vertical !== 'automotive' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Building2 className="w-3 h-3 text-violet-400" />Unidad de Interes</Label>
                <Select value={form.dev_unit_id || 'sin_definir'} onValueChange={v => {
                  if (v === 'sin_definir') {
                    setForm({ ...form, dev_unit_id: '', dev_unit_label: 'Sin definir', development_id: '' });
                  } else {
                    const unit = devUnits.find(u => u.id === v);
                    setForm({ ...form, dev_unit_id: v, dev_unit_label: unit ? `${unit.dev_name} - ${unit.label}` : '', development_id: unit?.development_id || '' });
                  }
                }}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-unit"><SelectValue placeholder="Seleccionar unidad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_definir">Sin definir</SelectItem>
                    {devUnits.map(u => <SelectItem key={u.id} value={u.id}>{u.dev_name} - {u.label} ({u.status})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Asignar a</Label>
              <Select value={form.assigned_to || 'none'} onValueChange={v => setForm({...form, assigned_to: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="opp-assigned"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Fecha esperada de cierre</Label><Input data-testid="opp-close-date" type="date" value={form.expected_close_date} onChange={e => setForm({...form, expected_close_date: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" /></div>
            {editing && oppAiActions.length > 0 && (
              <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.06] p-3 space-y-2" data-testid="opp-ai-skill-actions-card">
                <p className="text-xs font-semibold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" />Acciones de IA</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {[...oppAiActions].reverse().map((a, idx) => (
                    <div key={`${a.skill}-${idx}`} className="text-xs text-white/70 border-b border-white/5 pb-2 flex items-start justify-between gap-2" data-testid={`opp-ai-skill-action-${idx}`}>
                      <div className="min-w-0">
                        <p className="truncate">{a.label || a.skill}{a.value ? <span className="text-sky-300"> · {a.value}</span> : null}</p>
                        <p className="text-[10px] text-muted-foreground">{a.campaign_name || 'Campaña IA'} · {(a.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                      </div>
                      <Badge variant="outline" className={a.ok ? 'border-emerald-500/30 text-emerald-300 text-[9px] shrink-0' : 'border-white/15 text-muted-foreground text-[9px] shrink-0'}>{a.ok ? 'Ejecutada' : 'Pendiente'}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={save} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="save-lead-opp-btn">{editing ? 'Actualizar' : 'Crear Lead'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lost Reason Dialog - Multi-level */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto bg-[#1F2833] border-red-500/20">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-400 flex items-center gap-2"><XCircle className="w-5 h-5" />Marcar como Perdida</DialogTitle>
            <DialogDescription className="text-muted-foreground">Selecciona el motivo y sub-motivo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {lostTarget && (
              <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                <p className="text-sm text-white font-medium">{lostTarget.title}</p>
                <p className="text-xs text-muted-foreground">{formatPrice(lostTarget.value, lostTarget.currency)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-red-400 text-xs">Categoria *</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {lostReasons.map(reason => (
                  <button
                    key={reason.id} type="button"
                    onClick={() => setLostForm({ ...lostForm, lost_reason: reason.id, lost_sub_reason: '' })}
                    className={`text-left px-2.5 py-2 rounded-lg text-[11px] transition-all border ${
                      lostForm.lost_reason === reason.id ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-black/20 border-white/5 text-white/70 hover:border-white/10'}`}
                    data-testid={`lost-reason-${reason.id}`}
                  >{reason.label}</button>
                ))}
              </div>
            </div>
            {selectedReasonGroup?.sub_reasons?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-red-400/80 text-xs">Sub-motivo</Label>
                <div className="space-y-1">
                  {selectedReasonGroup.sub_reasons.map(sub => (
                    <button
                      key={sub.id} type="button"
                      onClick={() => setLostForm({ ...lostForm, lost_sub_reason: sub.id })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-[11px] transition-all border ${
                        lostForm.lost_sub_reason === sub.id ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-black/20 border-white/5 text-white/70 hover:border-white/10'}`}
                      data-testid={`lost-sub-${sub.id}`}
                    >{sub.label}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Detalle adicional (opcional)</Label>
              <Textarea value={lostForm.lost_reason_detail} onChange={e => setLostForm({ ...lostForm, lost_reason_detail: e.target.value })} rows={2} placeholder="Explica brevemente..." className="bg-black/20 border-white/10 focus:border-red-400" data-testid="lost-reason-detail" />
            </div>
            <Button onClick={confirmLost} className="w-full bg-red-500/80 hover:bg-red-500 text-white font-semibold" data-testid="confirm-lost-btn">Confirmar Perdida</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cold/Frio Dialog */}
      <Dialog open={coldDialogOpen} onOpenChange={setColdDialogOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-blue-500/20">
          <DialogHeader>
            <DialogTitle className="font-heading text-blue-400 flex items-center gap-2"><Snowflake className="w-5 h-5" />Congelar Oportunidad</DialogTitle>
            <DialogDescription className="text-muted-foreground">El lead necesita mas tiempo. Se reactivara automaticamente en la fecha que indiques.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {coldTarget && (
              <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                <p className="text-sm text-white font-medium">{coldTarget.title}</p>
                <p className="text-xs text-muted-foreground">{formatPrice(coldTarget.value, coldTarget.currency)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Fecha de Reactivacion *</Label>
              <Input type="date" value={coldForm.reactivation_date} onChange={e => setColdForm({...coldForm, reactivation_date: e.target.value})} className="bg-black/20 border-white/10 focus:border-blue-400" data-testid="cold-reactivation-date" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Volver a etapa</Label>
              <Select value={coldForm.return_stage_id} onValueChange={v => setColdForm({...coldForm, return_stage_id: v})}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="cold-return-stage"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{stages.filter(s => !s.is_lost_stage && !s.is_cold_stage).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Notas</Label>
              <Textarea value={coldForm.notes} onChange={e => setColdForm({...coldForm, notes: e.target.value})} rows={2} placeholder="Por que se congela?" className="bg-black/20 border-white/10 focus:border-blue-400" data-testid="cold-notes" />
            </div>
            <Button onClick={confirmCold} className="w-full bg-blue-500/80 hover:bg-blue-500 text-white font-semibold" data-testid="confirm-cold-btn">Congelar Oportunidad</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Offer Dialog */}
      <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-amber-500/20">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400 flex items-center gap-2"><DollarSign className="w-5 h-5" />Registrar Oferta</DialogTitle>
            <DialogDescription className="text-muted-foreground">Registra una oferta de precio para la negociacion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Monto</Label>
                <Input type="number" value={offerForm.amount} onChange={e => setOfferForm({...offerForm, amount: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 focus:border-amber-400" data-testid="offer-amount" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Ofrecido por</Label>
                <Select value={offerForm.offered_by} onValueChange={v => setOfferForm({...offerForm, offered_by: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="offer-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cliente">Cliente</SelectItem>
                    <SelectItem value="vendedor">Vendedor</SelectItem>
                    <SelectItem value="propietario">Propietario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Notas</Label>
              <Textarea value={offerForm.notes} onChange={e => setOfferForm({...offerForm, notes: e.target.value})} rows={2} className="bg-black/20 border-white/10 focus:border-amber-400" data-testid="offer-notes" />
            </div>
            <Button onClick={submitOffer} className="w-full bg-amber-500/80 hover:bg-amber-500 text-white font-semibold" data-testid="submit-offer-btn">Registrar Oferta</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Negotiation History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto bg-[#1F2833] border-amber-500/20">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><History className="w-5 h-5 text-amber-400" />Historial de Negociacion</DialogTitle>
            <DialogDescription className="text-muted-foreground">{historyTarget?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5" data-testid="quote-history-card">
              <p className="text-[11px] font-semibold text-cyan-300 mb-2">Historial de Cotizaciones (Lead 1:N)</p>
              {(historyTarget?.quote_history_backend?.length || historyTarget?.quotes_history_embedded?.length) ? (
                <div className="space-y-2">
                  {(historyTarget?.quote_history_backend?.length ? historyTarget.quote_history_backend : historyTarget.quotes_history_embedded || []).slice(0, 10).map((q, idx) => (
                    <div key={q.id || q.quote_id || idx} className="rounded-md border border-white/10 bg-black/20 px-2 py-1.5" data-testid={`quote-history-item-${idx}`}>
                      <p className="text-[11px] text-white">{q.unit_label || q.dev_unit_label || 'Unidad'} · ${(Number(q.total_price || 0)).toLocaleString('es-MX')} {q.currency || 'MXN'}</p>
                      <p className="text-[10px] text-muted-foreground">Mensual: ${(Number(q.monthly_payment || 0)).toLocaleString('es-MX')} · {q.created_at || q.sent_at ? new Date(q.created_at || q.sent_at).toLocaleDateString('es-MX') : '—'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Sin cotizaciones registradas todavía.</p>
              )}
            </div>
            {(!historyTarget?.negotiation_history || historyTarget.negotiation_history.length === 0) ? (
              <p className="text-xs text-muted-foreground text-center py-4">Sin ofertas registradas</p>
            ) : (
              historyTarget.negotiation_history.map((offer, i) => (
                <div key={offer.id || i} className={`p-3 rounded-lg border ${offer.status === 'aceptada' ? 'bg-emerald-500/[0.05] border-emerald-500/10' : offer.status === 'rechazada' ? 'bg-red-500/[0.05] border-red-500/10' : 'bg-black/20 border-white/5'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold text-white">${Number(offer.amount).toLocaleString('es-MX')} {offer.currency}</p>
                    <Badge variant="outline" className={`text-[9px] ${offer.status === 'aceptada' ? 'text-emerald-400 border-emerald-500/20' : offer.status === 'rechazada' ? 'text-red-400 border-red-500/20' : 'text-amber-400 border-amber-500/20'}`}>{offer.status}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Por: {offer.offered_by} | {offer.created_by} | {new Date(offer.created_at).toLocaleDateString('es-MX')}</p>
                  {offer.notes && <p className="text-[11px] text-white/60 mt-1 italic">{offer.notes}</p>}
                  {offer.status === 'pendiente' && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10" onClick={() => updateOfferStatus(historyTarget.id, offer.id, 'aceptada')}>Aceptar</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-500/20 text-red-400 hover:bg-red-500/10" onClick={() => updateOfferStatus(historyTarget.id, offer.id, 'rechazada')}>Rechazar</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-500/20 text-amber-400 hover:bg-amber-500/10" onClick={() => updateOfferStatus(historyTarget.id, offer.id, 'contraoferta')}>Contra</Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <LimitUpgradeDialog
        open={leadLimitDialogOpen}
        onOpenChange={setLeadLimitDialogOpen}
        title="Límite de leads alcanzado"
        description="No puedes crear más leads desde Oportunidades con tu plan actual."
        current={leads.length}
        limit={leadLimit < 0 ? 0 : leadLimit}
        testIdPrefix="opportunities-lead-limit"
      />
      <RegisterActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        leadId={activityContext.leadId}
        opportunityId={activityContext.opportunityId}
        leadName={activityContext.leadName}
      />
      <ActivitiesBellDialog
        open={bellOpen}
        onOpenChange={setBellOpen}
        user={user}
        products={properties || []}
        groups={devs || []}
        vendors={team || []}
        leads={leads || []}
      />
      {/* B2: mismo componente, fijado a UN lead vía leadId — botón en la
          tarjeta del Kanban (icono de campana junto a "Registrar actividad"). */}
      <ActivitiesBellDialog
        open={leadActivitiesView.open}
        onOpenChange={(v) => setLeadActivitiesView(prev => ({ ...prev, open: v }))}
        user={user}
        products={properties || []}
        vendors={team || []}
        leads={leads || []}
        leadId={leadActivitiesView.leadId}
        leadName={leadActivitiesView.leadName}
      />
    </div>
  );
}
